import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";

const Section = ({ num, title, children, testid }) => (
  <Reveal delay={0.05}>
    <div data-testid={testid}>
      <h2 className="font-display font-bold text-2xl text-slate-900 tracking-tight flex gap-4 items-baseline">
        <span className="font-mono text-xs text-ember">{num}</span>
        {title}
      </h2>
      <div className="mt-4 text-slate-600 leading-relaxed pl-9 space-y-4">{children}</div>
    </div>
  </Reveal>
);

const Terms = () => (
  <>
    <PageHeader
      testid="terms-header"
      overline="Terms of Use"
      title="The rules of"
      accent="this website."
      description="The services Concorde Auto Acceptance provides to you through this website are subject to the terms and conditions in this Terms of Use."
    />
    <section className="pb-24 lg:pb-32" data-testid="terms-body">
      <div className="mx-auto max-w-3xl px-6 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.2em] text-slate-600 uppercase" data-testid="terms-effective-date">
            Last updated October 30, 2018
          </p>
        </Reveal>
        <div className="mt-12 space-y-14">
          <Section num="01" title="Introduction" testid="terms-section-0">
            <p>
              Thank you for visiting our website. We hope that you find our
              website to be a useful tool for learning about our program and
              managing your account. Please note that the services Concorde Auto
              Acceptance provides to you through this website are subject to the
              terms and conditions in this Terms of Use, a contract between you
              and Concorde Auto Acceptance that explains the permissible and
              prohibited activities on this website. Please review this
              information carefully. In addition, please review our Privacy
              Policy to understand our practices and what we do with your
              information.
            </p>
          </Section>

          <Section num="02" title="Corrections" testid="terms-section-1">
            <p>
              If any information we have about you is incorrect or any
              information contained in this Terms of Use is inaccurate, we will
              gladly correct it. Email the incorrect information and the
              correction to{" "}
              <a href="mailto:virginia.sanjuan@concordeaa.com" className="text-ember font-medium hover:underline" data-testid="terms-corrections-email">
                virginia.sanjuan@concordeaa.com
              </a>
              , and we will attempt to correct the error. Please note that we
              reserve the right to verify the correctness of any information
              provided.
            </p>
          </Section>

          <Section num="03" title="Registration" testid="terms-section-2">
            <p>
              To obtain access to certain services on our website, you may be
              required to register. As part of any such registration process,
              you will be asked to provide your first and last name, and an
              email address and select a user name and a password. You agree
              that the information you supply during that registration process
              will be accurate and complete and that you will not register under
              the name of, nor attempt to enter the service under the name of,
              another person. We may require you to change your user name or
              password if we think it is necessary for security or other
              reasons. You will be responsible for preserving the
              confidentiality of your password and we strongly recommend that
              you do not disclose your password to anyone. We will never ask you
              for your password in any unsolicited communication, including
              unsolicited correspondence such as letters, phone calls, or e-mail
              messages. You agree to notify us of any known or suspected
              unauthorized use of your account.
            </p>
          </Section>

          <Section num="04" title="Acceptance of Terms & Conditions" testid="terms-section-3">
            <p>
              The terms and conditions in this Website Agreement govern your use
              of this website. By using this website, you agree that you have
              read, understand, accept, and are bound by all of the terms and
              conditions in this Website Agreement, and you agree not to use
              this website in contravention of these terms and conditions.
            </p>
            <p>
              Access to the website is provided for information and personal,
              non-commercial use only. Concorde Auto Acceptance reserves the
              right to amend any part of this website at any time without notice
              and without incurring any obligation unless prohibited by
              applicable law. Concorde Auto Acceptance will provide to you all
              notices as required by law in the event of such change. We
              encourage you to periodically review this Terms of Use and Website
              Agreement for amendments.
            </p>
          </Section>

          <Section num="05" title="Minors" testid="terms-section-4">
            <p>
              In compliance with the Children's Online Privacy Protection Act,
              Concorde Auto Acceptance does not knowingly or intentionally
              solicit or collect information from minors, and our marketing is
              not directed at minors. If we determine that a user is under
              eighteen (18) years of age and has submitted information by way of
              our website, we will immediately delete such information.
            </p>
          </Section>

          <Section num="06" title="Concorde Auto Acceptance Affiliates" testid="terms-section-5">
            <p>
              Concorde Auto Acceptance is part of a family of companies, or
              "affiliates." As of the date of this Notice, these companies
              include Concorde Auto Acceptance and Auto City. When
              we use the term Concorde Auto Acceptance, us, or we in this
              Website Agreement, it refers to Concorde Auto Acceptance and any
              or all of our affiliates.
            </p>
          </Section>

          <Section num="07" title="Accuracy of Website Information" testid="terms-section-6">
            <p>
              Concorde Auto Acceptance created this website to provide you with
              information about how our financing program works and how to
              manage your account. However, some inaccuracies may occasionally
              be present. Further, Concorde Auto Acceptance reserves the right
              to change product pricing and specifications, as well as the terms
              of our guarantees and warranties without notice. In addition, you
              understand and agree that the information provided on this website
              is advertising only and in no way constitutes an offer for the
              sale of goods or to provide a service. To this end, and to the
              extent permitted by applicable law, Concorde Auto Acceptance will
              not be responsible for errors found on this website.
            </p>
          </Section>

          <Section num="08" title="Ownership & Proprietary Rights" testid="terms-section-7">
            <p>
              All information contained in this website, unless otherwise
              stated, is owned solely and exclusively by Concorde Auto
              Acceptance. You acknowledge and agree that all contents and
              materials available on this website are protected by copyrights,
              trademarks, service marks, patents, trade secrets, and/or other
              proprietary or intellectual property rights and laws. By
              permitting you to access and use this website, Concorde Auto
              Acceptance has not granted you a license of any kind to use this
              information for any purpose (including, but not limited to,
              commercial purposes) without the express written consent of
              Concorde Auto Acceptance. You may not otherwise amend, copy,
              create derivative works from, reverse engineer, display,
              distribute, edit, license, modify, reproduce, rent, sell, and/or
              transmit any part of this website. You may not use any meta tags
              or any other "hidden text" utilizing Concorde Auto Acceptance's
              name or trademarks without the express written consent of Concorde
              Auto Acceptance. In addition, deep linking, direct linking,
              framing, page-jacking, spoofing, hacking, data-mining and using a
              robot, spider, or automated device of any kind to monitor or copy
              our website is strictly prohibited. You shall not upload to,
              distribute, or otherwise publish through this website any content,
              information, or other material that (a) violates or infringes the
              copyrights, patents, trademarks, service marks, trade secrets, or
              other proprietary rights of any person or entity; (b) is libelous,
              threatening, defamatory, obscene, indecent, pornographic, or could
              give rise to any civil or criminal liability under U.S. or
              international law; or (c) includes any bugs, viruses, worms, trap
              doors, Trojan horses or other harmful code or properties.
            </p>
          </Section>

          <Section num="09" title="No Unlawful or Prohibited Use" testid="terms-section-8">
            <p>
              As a condition of your use of Concorde Auto Acceptance's website,
              you warrant to use that you will not use the Website for any
              purpose that is unlawful or prohibited by these terms, conditions
              or notices. You may not use our Website in any manner that could
              damage, disable, overburden, or impair the Website or interfere
              with any other party's use or enjoyment of the Website, including
              in a manner that is described in the Ownership and Proprietary
              Rights section.
            </p>
          </Section>

          <Section num="10" title="Warranty Disclaimer" testid="terms-section-9">
            <p className="uppercase">
              Concorde Auto Acceptance makes no representations about the
              suitability of the information and related graphics contained in
              this website for any purpose whatsoever. All information provided
              on our website is provided "as is," with all faults and without
              warranties of any kind either express or implied. Concorde Auto
              Acceptance disclaims all warranties, express and implied,
              including, without limitation, those of merchantability, fitness
              for a particular purpose, title and non-infringement and any
              warranties arising from course of performance, course of dealing,
              or usage in trade. Concorde Auto Acceptance shall not be liable
              for any indirect, special, consequential, incidental, or punitive
              damages, including, without limitation, lost profits or revenues,
              costs of replacement of goods, loss or damage to data arising out
              of the use or inability to use the Concorde Auto Acceptance
              website, or for damage resulting from the use of or reliance on
              the information contained herein. Some states do not allow
              limitations on how long an implied warranty lasts or exclusion or
              limitations on relief such as incidental or consequential damages,
              so the above limitation may not apply to you.
            </p>
          </Section>

          <Section num="11" title="Usage and Ownership of Customer Information" testid="terms-section-10">
            <p>
              All information submitted to Concorde Auto Acceptance through our
              website becomes and remains the property of Concorde Auto
              Acceptance. Consequently, we may use any information you submit
              for any purpose we deem appropriate in accordance with this Terms
              of Use and our Privacy Policy. We maintain strict physical,
              electronic and administrative safeguards to protect your personal
              information from unauthorized or inappropriate access. We restrict
              access to information about you to those Concorde Auto Acceptance
              employees and third-party affiliates who need to know the
              information to respond to your request or inquiry.
            </p>
          </Section>

          <Section num="12" title="Links to Third Party Websites" testid="terms-section-11">
            <p>
              While using this website, you may choose to link to third-party
              websites that are not affiliated with Concorde Auto Acceptance.
              Concorde Auto Acceptance is not responsible for the content of
              these third-party websites, or any subsequent links, and has no
              control over the contents found there. For this reason, we do not
              represent or warrant that the contents of any third-party website
              are accurate or compliant with state or federal law, or compliant
              with copyright or other intellectual property laws. All opinions
              expressed on third-party websites are not necessarily those of
              Concorde Auto Acceptance, and Concorde Auto Acceptance does not
              endorse the contents of these third-party websites in any way.
              Also, Concorde Auto Acceptance is not responsible for webcasting
              or any other form of transmission received from any linked
              website. Any reliance on the contents of a third-party website is
              done at your own risk, and you assume all responsibilities and
              consequences resulting from such reliance. This may include, but
              is not limited to, damages that are direct, indirect, special, or
              consequential. Concorde Auto Acceptance is providing these links
              to you as a convenience only, and the inclusion of any link does
              not imply endorsement of the site, its contents, and/or any
              products or services offered by the owner(s) of the site.
            </p>
          </Section>

          <Section num="13" title="Mobile Access" testid="terms-section-12">
            <p>
              The website may include access and services available from a
              mobile device, including the ability to access the website from
              your mobile device; the ability to upload to the website from a
              mobile device; and the ability to receive and reply to messages,
              and send content and messages using chat software and text
              messaging. Concorde Auto Acceptance does not charge a fee for
              these mobile services. Your mobile device carrier's normal
              messaging, data, and other rates and fees still apply. You should
              contact your carrier for information on mobile usage rates and
              fees. By using Concorde Auto Acceptance's mobile services, you
              agree that Concorde Auto Acceptance can communicate with you by
              SMS, MMS, text messages and/or other electronic means to your
              mobile device and that certain information about your usage of
              Concorde Auto Acceptance's mobile services may be communicated to
              Concorde Auto Acceptance.
            </p>
          </Section>

          <Section num="14" title="International Access" testid="terms-section-13">
            <p>
              This website may be accessed from countries other than the United
              States and may contain products and services or references to
              products and services, which are not available outside of the
              United States. Any such references do not imply that such products
              will be made available outside the United States. If you access
              and use this Site outside the United States you are responsible
              for complying with your local laws and regulations.
            </p>
          </Section>

          <Section num="15" title="Legal Disputes and Choice of Law" testid="terms-section-14">
            <p>
              If there is a legal dispute of any kind about or arising out of
              the use of this website, the laws of the State of Texas will
              govern such dispute without regard to its conflict of laws rules.
              You agree that if any part of these terms and conditions is found
              to be unenforceable, the remainder of these terms and conditions
              will remain in full force and effect. By using this website, you
              expressly agree and submit to personal jurisdiction and exclusive
              venue in the state and federal courts located in the State of
              Texas, County of Dallas.
            </p>
          </Section>

          <Section num="16" title="Violations of Website Agreement" testid="terms-section-15">
            <p>
              In the event you violate or attempt to violate any part of this
              Website Agreement, Concorde Auto Acceptance reserves the right to
              terminate your access to this website immediately, at our sole
              discretion, with or without notice of any kind. We also reserve
              the right to involve and cooperate with law enforcement agencies,
              and to pursue a civil lawsuit or criminal prosecution for any and
              all alleged or actual illegal activities involving this website.
            </p>
          </Section>

          <Section num="17" title="Indemnification" testid="terms-section-16">
            <p>
              You agree to indemnify, defend, and hold harmless Concorde Auto
              Acceptance from and against any and all claims, proceedings,
              damages, injuries, losses, costs, and expenses (including
              reasonable attorneys' fees), relating to your use of this website
              that leads to any claim against us by a third-party, regardless of
              the nature of the cause of action or claim.
            </p>
          </Section>

          <Section num="18" title="Severability and Integration" testid="terms-section-17">
            <p>
              In the event any part of this Website Agreement is found to be
              unenforceable, the remainder shall continue in full force and
              effect. You understand and agree that this Website Agreement
              represents the entire agreement governing your use of this
              website.
            </p>
          </Section>

          <Section num="19" title="Reservation of Rights" testid="terms-section-18">
            <p>
              Any and all rights not expressly granted to you in this Terms of
              Use and this Website Agreement are hereby reserved by Concorde
              Auto Acceptance.
            </p>
          </Section>

          <Section num="20" title="Questions" testid="terms-section-19">
            <p>
              If you have any questions about these Terms of Use, please contact
              Compliance Officer, 908 Buckner, Dallas, TX 75271 or{" "}
              <a href="mailto:virginia.sanjuan@concordeaa.com" className="text-ember font-medium hover:underline" data-testid="terms-questions-email">
                virginia.sanjuan@concordeaa.com
              </a>
              . Please include enough information so that we are able help you,
              such as your name, phone number or email address, and the specific
              website mobile site, application, and/or other service you are
              contacting us about.
            </p>
          </Section>
        </div>
      </div>
    </section>
  </>
);

export default Terms;
